import { inject, Injectable } from '@angular/core';
import {
  KEY_MILESTONES_TABLE_COLUMN_KEY_LABEL_MAP,
  KPI_CHART_CONSTANTS,
  KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP,
  KPI_EARLY_TERM_REASON_MAP,
  KPI_EREQ_USAGE_FOR_LAB_SAMPLES_BY_REQUEST_TYPE_MAP,
  KPI_KEYS_TO_CHART_TYPES_MAP,
  KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP,
  STUDY_KPI_KEYS_TO_NAMES_MAP,
  STUDY_KPI_KEYS_TO_TYPE_MAPPING,
  TIME_RANGE_MAP
} from '@catalogues';
import {
  AggregatedValue,
  AxisCalculationElement,
  DrilldownTableColumnConfig,
  HistoricalKpi,
  KeyMilestonesTableData,
  KeyMilestonesTableRow,
  Kpi,
  KpiBarChartConstants,
  KpiCategoricalChartData,
  KpiCategoricalChartType,
  KpiChartAggregateFilter,
  KpiChartCondition,
  KpiChartConfig,
  KpiChartData,
  KpiChartDataConfig,
  KpiChartDataResponse,
  KpiChartDataType,
  KpiChartDataValueAggregator,
  KpiChartDataValueFilter,
  KpiChartId,
  KpiChartOperation,
  KpiCharts,
  KpiChartSeedData,
  KpiChartSrcDataFilter,
  KpiConfig,
  KpiGroupedBarChartConstants,
  KpiKeys,
  KpiNames,
  KpiPieChartConstants,
  KpisByCategory,
  KpisByKey,
  KpiTimeCycleChartData,
  KpiToggleChart,
  KpiToggleChartConstants,
  KpiToggleChartData,
  KpiTypes,
  MilestoneStage,
  TimeCycleChart,
  ToggleLabel,
  TrendChartData,
  TrendChartSeries,
  ValueCalculationElement
} from '@models';

import { CommonFunctionsService } from '../common-functions/common-functions.service';

@Injectable({
  providedIn: 'root'
})
export class KpiChartCalculationService {
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);
  KPI_CHARTS_WITH_GROUPED_VALUES: KpiChartId[] = [
    'pieSubjectScreenFailRateByReason',
    'pieEarlyTermByReason',
    'pieDeviationRatePerSubjectByReason',
    'pieDaysToCloseSiteFindingsByTimeRange',
    'pieDaysToPaySitesByTimeRange',
    'pieLabSampleQueryRateByQueryType',
    'pieEreqUsageForLabSamplesBySampleType',
    'pieLabKitsShippedByKitType',
    'pieLabKitWastageByKitType'
  ];
  KPI_NUMERATOR_DENOMINATOR_FIELD = 'numerator/denominator';
  KPI_CHARTS_WITH_GROUPED_VALUES_AND_NO_NUMERATOR_DENOMINATOR: string[] = [
    'barDaysToCloseSiteFindingsByTimeRange',
    'pieDaysToCloseSiteFindingsByTimeRange',
    'pieSubjectScreenFailRateByReason',
    'pieDeviationRatePerSubjectByReason',
    'barDaysToPaySitesByTimeRange',
    'pieDaysToPaySitesByTimeRange',
    'barEreqUsageForLabSamplesByRequestType'
  ];

  initializeDefaultKpiChartData(
    kpiConfig: KpiConfig,
    kpisByCategory: KpisByCategory,
    milestoneStages: MilestoneStage[]
  ): Partial<Record<KpiKeys, KpiChartData>> {
    const kpiChartData: Partial<Record<KpiKeys, KpiChartData>> = {};

    (Object.values(kpisByCategory) as KpisByKey[]).forEach((kpisByKey) => {
      (Object.entries(kpisByKey) as [KpiKeys, Kpi][]).forEach(([kpiKey, kpiValue]) => {
        kpiChartData[kpiKey] = {
          name: kpiValue.name,
          dataLoaded: false,
          charts: this.initializeKpiCharts(kpiConfig, kpiKey, kpiValue, milestoneStages),
          chartSeedData: { unfiltered: undefined, filtered: undefined }
        };
      });
    });
    return kpiChartData;
  }

  setTrendChartData(
    trendChartData: TrendChartData,
    historicalData: HistoricalKpi[],
    kpiKey: KpiKeys,
    tooltipText?: string
  ): void {
    const kpiType: KpiTypes = STUDY_KPI_KEYS_TO_TYPE_MAPPING[kpiKey]!;
    const kpiName: KpiNames = STUDY_KPI_KEYS_TO_NAMES_MAP[kpiKey]!;

    const originalTrendChartSeries: TrendChartSeries = {
      name: kpiName,
      data: [],
      hasMarkLine: true
    };
    const numeratorTrendChartSeries: TrendChartSeries = {
      name: kpiName,
      data: [],
      hasMarkLine: false
    };
    const denominatorTrendChartSeries: TrendChartSeries = {
      name: 'On track',
      data: [],
      hasMarkLine: false
    };

    historicalData.forEach((data) => {
      originalTrendChartSeries.data.push(this.setDataValue(data.value, kpiKey, kpiType));

      if (data.numerator !== undefined) {
        numeratorTrendChartSeries.data.push(data.numerator);
      }
      if (data.denominator !== undefined) {
        denominatorTrendChartSeries.data.push(data.denominator);
      }

      trendChartData.xAxisLabels.push(this.setXAxisLabel(data.date));
    });

    originalTrendChartSeries.data.reverse();
    numeratorTrendChartSeries.data.reverse();
    denominatorTrendChartSeries.data.reverse();

    trendChartData.series.push(
      originalTrendChartSeries,
      numeratorTrendChartSeries,
      denominatorTrendChartSeries
    );

    trendChartData.xAxisLabels.reverse();
    // check if tooltipText is not 'NULL'
    trendChartData.tooltipText =
      tooltipText !== 'NULL' && tooltipText !== undefined ? tooltipText : '';
  }

  setTimeCycleData(kpiChartData: KpiChartData, timeCycleResponse: TimeCycleChart): void {
    const timeCycleChart = kpiChartData.charts.timeCycleChart;
    if (!timeCycleChart) return;

    timeCycleChart.labels = timeCycleResponse.labels;
    timeCycleChart.offset = timeCycleResponse.offset;
    timeCycleChart.timeWeeks = timeCycleResponse.timeWeeks;
    timeCycleChart.category = timeCycleResponse.category;
    timeCycleChart.cycleCount = timeCycleResponse.cycleCount;
  }

  private initializeKpiCharts(
    kpiConfig: KpiConfig,
    kpiKey: KpiKeys,
    kpiValue: Kpi,
    milestoneStages: MilestoneStage[]
  ): KpiCharts {
    const kpiCharts: KpiCharts = {
      trendchart: this.initializeEmptyTrendChartData(kpiConfig, kpiKey, kpiValue)
    };

    const kpiChartConfigs: KpiChartConfig[] | undefined = KPI_KEYS_TO_CHART_TYPES_MAP[kpiKey];
    if (!kpiChartConfigs) {
      console.warn(`No chart configuration found for KPI key: ${kpiKey}`);
      return kpiCharts;
    }

    kpiChartConfigs.forEach((kpiChartConfig) => {
      switch (kpiChartConfig.type) {
        case 'keyMilestonesTable':
          kpiCharts['keyMilestonesTable'] = this.getKeyMilestonesTableData(milestoneStages);
          return;
        case 'toggleChart': {
          if (!kpiCharts.toggleChart) kpiCharts.toggleChart = [];
          const toggleChartData = this.initializeToggleChartData(kpiChartConfig);
          if (toggleChartData) kpiCharts.toggleChart.push(toggleChartData);
          return;
        }
        case 'pie':
          if (!kpiCharts.pie) kpiCharts.pie = [];
          kpiCharts.pie.push(this.initializePieChartData(kpiChartConfig.id));
          return;
        case 'bar':
          if (!kpiCharts.bar) kpiCharts.bar = [];
          kpiCharts.bar.push(this.initializeBarChartData(kpiChartConfig.id));
          return;
        case 'groupedBar':
          if (!kpiCharts.groupedBar) kpiCharts.groupedBar = [];
          kpiCharts.groupedBar.push(this.initializeGroupedBarChartData(kpiChartConfig.id));
          return;
        case 'timeCycleChart':
          kpiCharts['timeCycleChart'] = this.initializeTimeCycleChartData(kpiChartConfig.id);
          return;
        // Add other cases here
        default:
          return;
      }
    });

    return kpiCharts;
  }

  private initializeToggleChartData(chartConfig: KpiChartConfig): KpiToggleChartData | undefined {
    const constants = KPI_CHART_CONSTANTS[chartConfig.id] as KpiToggleChartConstants;
    if (!constants) return;

    const buildChart = (
      chart: { id: KpiChartId; label: ToggleLabel; type: KpiCategoricalChartType },
      selected: boolean
    ): KpiToggleChart => ({
      id: chart.id,
      selected,
      label: chart.label,
      data: this.initializeSupportedToggleCharts(chart.type, chart.id)
    });

    return {
      identifier: chartConfig.id,
      titleSuffix: constants.titleSuffix,
      tooltipText: constants.tooltipText,
      charts: {
        primaryChart: buildChart(constants.charts.primaryChart, true),
        secondaryChart: buildChart(constants.charts.secondaryChart, false)
      }
    };
  }

  private initializeSupportedToggleCharts(
    chartType: KpiCategoricalChartType,
    chartId: KpiChartId
  ): KpiCategoricalChartData {
    switch (chartType) {
      case 'bar':
        return this.initializeBarChartData(chartId);
      case 'pie':
        return this.initializePieChartData(chartId);
      case 'groupedBar':
        return this.initializeGroupedBarChartData(chartId);
    }
  }

  private initializePieChartData(kpiChartId: KpiChartId): KpiCategoricalChartData {
    const chartConstants = KPI_CHART_CONSTANTS[kpiChartId] as KpiPieChartConstants;
    return {
      titleSuffix: chartConstants?.titleSuffix || '',
      type: 'pie',
      identifier: kpiChartId,
      values: {} as Record<string, AggregatedValue>,
      config: undefined,
      sort: chartConstants?.sortValue || 'none',
      tooltipText: chartConstants?.tooltipText || '',
      onTrack: undefined,
      onTarget: undefined,
      fieldFilter: chartConstants?.fieldFilter,
      legendValues: chartConstants?.legendValues || [],
      hasXAxisAggLabel: false
    };
  }

  private initializeBarChartData(kpiChartId: KpiChartId): KpiCategoricalChartData {
    const chartConstants = KPI_CHART_CONSTANTS[kpiChartId] as KpiBarChartConstants;

    return {
      titleSuffix: chartConstants?.titleSuffix || '',
      type: 'bar',
      identifier: kpiChartId,
      values: {} as Record<string, AggregatedValue>,
      config: undefined,
      xAxisLabel: chartConstants?.xAxisLabel || '',
      sort: chartConstants?.sortValue || 'none',
      tooltipText: chartConstants?.tooltipText || '',
      yAxisLabelCount: chartConstants?.labelCount || {},
      onTrack: undefined,
      onTarget: undefined,
      fieldFilter: chartConstants?.fieldFilter,
      legendValues: chartConstants?.legendValues || [],
      hasXAxisAggLabel: false
    };
  }

  private initializeGroupedBarChartData(kpiChartId: KpiChartId): KpiCategoricalChartData {
    const chartConstants = KPI_CHART_CONSTANTS[kpiChartId] as KpiGroupedBarChartConstants;

    return {
      titleSuffix: chartConstants?.titleSuffix || '',
      type: 'groupedBar',
      identifier: kpiChartId,
      values: {} as Record<string, AggregatedValue>,
      config: undefined,
      xAxisLabel: chartConstants?.xAxisLabel || '',
      sort: chartConstants?.sortValue || 'none',
      tooltipText: chartConstants?.tooltipText || '',
      yAxisLabelCount: chartConstants?.labelCount || {},
      onTrack: undefined,
      onTarget: undefined,
      fieldFilter: chartConstants?.fieldFilter,
      legendValues: chartConstants?.legendValues || [],
      hasXAxisAggLabel: false
    };
  }

  private initializeTimeCycleChartData(kpiChartId: KpiChartId): KpiTimeCycleChartData {
    return {
      identifier: kpiChartId,
      tooltipText:
        'Cycle time is the elapsed time from the first milestone’s start to the second milestone’s completion.',
      title: 'Cycle time',
      labels: [],
      offset: [],
      timeWeeks: [],
      category: [],
      cycleCount: 0
    };
  }

  private getKeyMilestonesTableData(milestoneStages: MilestoneStage[]): KeyMilestonesTableData {
    return {
      identifier: 'keyMilestonesTable',
      title: 'Milestone summary table',
      columns: this.getKeyMilestonesTableColumns(),
      rows: this.getKeyMilestonesTableRows(milestoneStages),
      tooltipInfo: 'KPI Tooltip info coming soon'
    };
  }

  private getKeyMilestonesTableColumns(): DrilldownTableColumnConfig<KeyMilestonesTableRow>[] {
    const alwaysVisibleColumns: (keyof KeyMilestonesTableRow)[] = ['milestoneName', 'status'];
    const dateColumns: (keyof KeyMilestonesTableRow)[] = [
      'actualDate',
      'baselineDate',
      'plannedDate'
    ];
    return (
      Object.entries(KEY_MILESTONES_TABLE_COLUMN_KEY_LABEL_MAP) as [
        keyof KeyMilestonesTableRow,
        string
      ][]
    ).map(([key, label]) => {
      const isAlwaysVisible = alwaysVisibleColumns.includes(key);

      return {
        key,
        label,
        alwaysVisible: isAlwaysVisible,
        visibleSizes: isAlwaysVisible ? undefined : ['medium', 'large'],
        isBadge: key === 'status',
        isDate: dateColumns.includes(key)
      };
    });
  }

  private getKeyMilestonesTableRows(milestoneStages: MilestoneStage[]): KeyMilestonesTableRow[] {
    const rows: KeyMilestonesTableRow[] = [];
    this.commonFunctionsService
      .getMilestonesFlatMapFromStages(milestoneStages)
      .forEach((milestone) => {
        if (milestone.isKeyMilestone) {
          rows.push({
            milestoneName: milestone.name,
            status: milestone.status,
            baselineDate: milestone.values.baseline,
            plannedDate: milestone.values.planned,
            actualDate: milestone.values.actual,
            baselineVariance: this.getBaselineVarianceWording(milestone.baselineToActual)
          });
        }
      });

    return rows;
  }

  private getBaselineVarianceWording(baselineToActual: number | null): string | null {
    if (baselineToActual === null) return null;
    const daysWording = Math.abs(baselineToActual) === 1 ? 'day' : 'days';

    return `${baselineToActual} ${daysWording}`;
  }

  private setDataValue(value: number, kpiKey: KpiKeys, kpiType: KpiTypes): number {
    switch (kpiType) {
      case 'percent':
        return parseFloat((value * 100).toFixed(1));
      case 'days':
        return Math.trunc(value);
      case 'number':
        if (kpiKey == 'deviation_rate_per_subject') return parseFloat(value.toFixed(2));
        break;
      default:
        return value;
    }
    return value;
  }

  private setXAxisLabel(date: string): string {
    return this.commonFunctionsService.formatDate(date);
  }

  private initializeEmptyTrendChartData(
    kpiConfig: KpiConfig,
    kpiKey: KpiKeys,
    kpi: Kpi
  ): TrendChartData {
    return {
      identifier: 'trendChart',
      titleSuffix: 'Over time',
      xAxisName: 'Date',
      yAxisName: '',
      xAxisLabels: [],
      yAxisLabels: [],
      targetValue: kpiConfig[kpiKey]?.targetValue,
      overTarget: kpiConfig[kpiKey]?.overTarget,
      series: [],
      legend: this.getLegend(kpiConfig, kpi.name, kpiKey),
      seriesName: kpi.name,
      isPercentChart: this.isPercentChart(kpi.type),
      isCount: false,
      hasToggle: this.trendChartHasToggle(kpi.type),
      tooltipText: ''
    };
  }

  private getLegend(kpiConfig: KpiConfig, kpiName: KpiNames, kpiKey: KpiKeys): string[] {
    const legend: string[] = ['KPI'];

    if (kpiConfig[kpiKey]?.targetValue) {
      legend.push('On track');
    }
    return legend;
  }

  private isPercentChart(kpiType: KpiTypes): boolean {
    return kpiType === 'percent';
  }

  private trendChartHasToggle(kpiType: KpiTypes) {
    return kpiType == 'percent';
  }

  setKpiChartData(kpiKey: KpiKeys, response: KpiChartDataResponse, kpiCharts: KpiChartData) {
    // we need to set the values, categories and the config for each chart depending on the kpiKey
    const seedData = kpiCharts.chartSeedData;
    if (seedData && seedData.unfiltered === undefined) {
      seedData.unfiltered = response.data;
    }

    const kpiChartConfigs: KpiChartConfig[] | undefined = KPI_KEYS_TO_CHART_TYPES_MAP[kpiKey];
    if (!kpiChartConfigs) {
      console.warn(`No chart configuration found for KPI key: ${kpiKey}`);
      return;
    }

    kpiChartConfigs.forEach((kpiChartConfig) => {
      switch (kpiChartConfig.type) {
        case 'toggleChart': {
          const currentChartData = kpiCharts.charts[kpiChartConfig.type]?.find(
            (chart) => chart.identifier === kpiChartConfig.id
          ) as KpiToggleChartData;

          if (!currentChartData) return;

          const primaryChartConfig = response.config.find(
            (item) => item.name === currentChartData.charts.primaryChart.id
          );
          this.tryCalculateKpiChartData(
            kpiKey,
            currentChartData.charts.primaryChart.data,
            primaryChartConfig,
            seedData
          );

          const secondaryChartConfig = response.config.find(
            (item) => item.name === currentChartData.charts.secondaryChart.id
          );
          this.tryCalculateKpiChartData(
            kpiKey,
            currentChartData.charts.secondaryChart.data,
            secondaryChartConfig,
            seedData
          );

          return;
        }
        case 'bar':
        case 'groupedBar':
        case 'pie': {
          // find kpiChartType in response.config array where each array item has a name field that matches kpiChartType
          const config = response.config.find((item) => item.name === kpiChartConfig.id);
          const currentChartData = kpiCharts.charts[kpiChartConfig.type]?.find(
            (chart) => chart.identifier === kpiChartConfig.id
          );

          this.tryCalculateKpiChartData(kpiKey, currentChartData, config, seedData);

          return;
        }
        // Add other cases here
        default:
          return;
      }
    });
  }

  private tryCalculateKpiChartData(
    kpiKey: KpiKeys,
    chartData?: KpiCategoricalChartData,
    config?: KpiChartDataConfig,
    seedData?: KpiChartSeedData
  ): void {
    if (config && chartData && seedData?.filtered) {
      this.calculateKpiChartData(chartData, config, seedData.filtered);
    } else if (config && chartData && seedData?.unfiltered) {
      this.calculateKpiChartData(chartData, config, seedData.unfiltered);
    }
  }

  getGroupByValue(
    config: KpiChartDataConfig,
    axisAggregator: KpiChartDataValueAggregator,
    item: KpiChartDataType
  ) {
    let groupByValue;
    const groupingMaps = {
      barSubjectScreenFailRateByReason: KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP,
      barEarlyTermByReason: KPI_EARLY_TERM_REASON_MAP,
      barDeviationRatePerSubjectByReason: KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP,
      barDaysToCloseSiteFindingsByTimeRange: TIME_RANGE_MAP,
      barDaysToPaySitesByTimeRange: TIME_RANGE_MAP,
      barEreqUsageForLabSamplesByRequestType: KPI_EREQ_USAGE_FOR_LAB_SAMPLES_BY_REQUEST_TYPE_MAP
    };

    const groupingMap = groupingMaps[config.name as keyof typeof groupingMaps];
    if (groupingMap) {
      groupByValue = groupingMap[axisAggregator.groupBy!];
    } else {
      groupByValue = (item as Record<string, any>)[axisAggregator.groupBy!];
    }
    return groupByValue;
  }

  private calculateKpiChartData(
    kpiChart: KpiCategoricalChartData,
    config: KpiChartDataConfig,
    data: KpiChartDataType[]
  ) {
    kpiChart.config = config;
    this.setTargetValues(config, kpiChart);
    const aggregatedValues: Record<string, AggregatedValue> = {};
    const aggregationAndFilterConfig = this.extractConfig(config);

    const axisCalculationElements = aggregationAndFilterConfig?.axisCalculationElements;
    const valueCalculationElements = aggregationAndFilterConfig?.valueCalculationElements;
    const dataFilters = aggregationAndFilterConfig?.dataFilters;

    const {
      numeratorValuesMap,
      numeratorCountMap,
      denominatorValuesMap,
      denominatorCountMap,
      filterMap,
      organizationCountryMap,
      regularValueMap,
      regularCountMap,
      countFieldTrackerArray,
      distinctGroupedValueMap
    }: {
      numeratorValuesMap: Map<string, number>;
      numeratorCountMap: Map<string, number>;
      denominatorValuesMap: Map<string, number>;
      denominatorCountMap: Map<string, number>;
      filterMap: Map<string, KpiChartAggregateFilter>;
      organizationCountryMap: Map<string, string>;
      regularValueMap: Map<string, number>;
      regularCountMap: Map<string, number>;
      countFieldTrackerArray: string[];
      distinctGroupedValueMap: Map<string, Set<number>>;
    } = this.setupKpiCalculationMaps();

    // apply data filters first to remove unwanted data points
    data = this.applyDataFilters(dataFilters, data);

    if (config.type === 'bar' || config.type === 'groupedBar') {
      data.forEach((item) => {
        // handle multiple xAxis definitions, bars should have at least 1
        axisCalculationElements?.forEach((axisCalculationElement) => {
          const numeratorAggregator = axisCalculationElement.numerator;
          const denominatorAggregator = axisCalculationElement.denominator;
          const aggregateFilter = axisCalculationElement.filter;
          const axisAggregator = axisCalculationElement.axisAggregator;
          const hasAggLabel = axisCalculationElement.hasAggLabel;

          kpiChart.hasXAxisAggLabel = hasAggLabel;
          // if both numerator and denominator nested aggregators are defined, we need to populate both numerator and denominator value maps
          if (numeratorAggregator && denominatorAggregator) {
            this.populateAggregatorMaps(
              numeratorAggregator,
              item,
              config,
              numeratorValuesMap,
              numeratorCountMap,
              denominatorAggregator,
              denominatorValuesMap,
              denominatorCountMap,
              aggregateFilter,
              filterMap,
              kpiChart,
              organizationCountryMap
            );
          }
          // xAxis might be defined with a different calculation other than numerator/denominator so handle that case
          if (
            axisAggregator !== undefined &&
            axisAggregator.field !== undefined &&
            axisAggregator.field !== this.KPI_NUMERATOR_DENOMINATOR_FIELD
          ) {
            const numeratorValue = (item as Record<string, any>)[axisAggregator.field];
            /* barSubjectScreenFailRateByReason / barEarlyTermByReason / barDeviationRatePerSubjectByReason / barDaysToCloseSiteFindingsByTimeRange 
              do not have a normal group_by instead it should get the group by value from a map */
            const numeratorGroupByValue = this.getGroupByValue(config, axisAggregator, item);

            this.populateNumeratorAggregatorMaps(
              axisAggregator.operation,
              numeratorValue,
              numeratorGroupByValue,
              regularValueMap,
              regularCountMap,
              countFieldTrackerArray
            );

            if (aggregateFilter) {
              this.updateFilterMap(item, aggregateFilter, filterMap, config);
              this.populateOrganizationCountryMap(
                kpiChart.identifier,
                item,
                organizationCountryMap
              );
            }
          }
        });
      });
    } else if (config.type === 'pie') {
      data.forEach((item) => {
        valueCalculationElements?.forEach((valueCalculationElement) => {
          const numeratorAggregator = valueCalculationElement.numerator;
          const denominatorAggregator = valueCalculationElement.denominator;
          const aggregateFilter = valueCalculationElement.filter;
          const valueAggregator = valueCalculationElement.valueAggregator;

          if (numeratorAggregator && denominatorAggregator) {
            this.populateAggregatorMaps(
              numeratorAggregator,
              item,
              config,
              numeratorValuesMap,
              numeratorCountMap,
              denominatorAggregator,
              denominatorValuesMap,
              denominatorCountMap,
              aggregateFilter,
              filterMap,
              kpiChart,
              organizationCountryMap
            );
          }
          // only if it does not have numerator/denominator as operation such as count, aggregate values the old way
          if (
            valueAggregator !== undefined &&
            valueAggregator.field !== this.KPI_NUMERATOR_DENOMINATOR_FIELD
          ) {
            this.aggregatePieValues(
              item,
              valueAggregator,
              distinctGroupedValueMap,
              config,
              regularValueMap
            );
          }

          if (aggregateFilter) {
            this.updateFilterMap(item, aggregateFilter, filterMap, config);
          }
        });
      });
    }

    if (config.type === 'bar' || config.type === 'groupedBar') {
      this.processBarChartValues(
        axisCalculationElements,
        config,
        numeratorValuesMap,
        denominatorValuesMap,
        numeratorCountMap,
        denominatorCountMap,
        aggregatedValues,
        regularValueMap,
        regularCountMap,
        filterMap,
        organizationCountryMap,
        kpiChart
      );
    } else if (config.type === 'pie') {
      if (this.KPI_CHARTS_WITH_GROUPED_VALUES.includes(kpiChart.identifier)) {
        this.processPieChartValues(
          valueCalculationElements,
          config,
          numeratorValuesMap,
          denominatorValuesMap,
          numeratorCountMap,
          denominatorCountMap,
          aggregatedValues,
          regularValueMap,
          regularCountMap,
          filterMap,
          organizationCountryMap,
          kpiChart
        );
      } else if (distinctGroupedValueMap.size > 0) {
        this.processSimplePieChartValues(distinctGroupedValueMap, config, aggregatedValues);
      }
    }

    // sort values
    const sortedValues = this.sortValues(kpiChart, aggregatedValues, config.type);
    kpiChart.values = Object.fromEntries(Object.entries(sortedValues));
  }

  private applyDataFilters(
    dataFilters: KpiChartSrcDataFilter[] | undefined,
    data: KpiChartDataType[]
  ) {
    if (dataFilters && dataFilters.length > 0) {
      data = data.filter((item) => {
        return dataFilters!.every((filter) => {
          const fieldValue = (item as Record<string, any>)[filter.field];
          return this.evaluateDataFilterCondition(fieldValue, filter);
        });
      });
    }
    return data;
  }

  private setupKpiCalculationMaps() {
    const numeratorValuesMap = new Map<string, number>();
    const numeratorCountMap = new Map<string, number>();
    const denominatorValuesMap = new Map<string, number>();
    const denominatorCountMap = new Map<string, number>();

    const regularValueMap = new Map<string, number>();
    const distinctGroupedValueMap = new Map<string, Set<number>>();
    const regularCountMap = new Map<string, number>();
    const countFieldTrackerArray: string[] = [];

    const filterMap = new Map<string, KpiChartAggregateFilter>();
    const organizationCountryMap = new Map<string, string>();
    return {
      numeratorValuesMap,
      numeratorCountMap,
      denominatorValuesMap,
      denominatorCountMap,
      filterMap,
      organizationCountryMap,
      regularValueMap,
      regularCountMap,
      countFieldTrackerArray,
      distinctGroupedValueMap
    };
  }

  private populateAggregatorMaps(
    numeratorAggregator: KpiChartDataValueAggregator,
    item: KpiChartDataType,
    config: KpiChartDataConfig,
    numeratorValuesMap: Map<string, number>,
    numeratorCountMap: Map<string, number>,
    denominatorAggregator: KpiChartDataValueAggregator,
    denominatorValuesMap: Map<string, number>,
    denominatorCountMap: Map<string, number>,
    aggregateFilter: KpiChartDataValueFilter | undefined,
    filterMap: Map<string, KpiChartAggregateFilter>,
    kpiChart: KpiCategoricalChartData,
    organizationCountryMap: Map<string, string>
  ) {
    const numeratorValue =
      numeratorAggregator.field !== undefined
        ? (item as Record<string, any>)[numeratorAggregator.field]
        : undefined;
    let numeratorGroupByValue;
    const groupingMaps = {
      barSubjectScreenFailRateByReason: KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP,
      pieSubjectScreenFailRateByReason: KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP,
      barEarlyTermByReason: KPI_EARLY_TERM_REASON_MAP,
      pieEarlyTermByReason: KPI_EARLY_TERM_REASON_MAP,
      barDeviationRatePerSubjectByReason: KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP,
      pieDeviationRatePerSubjectByReason: KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP
    };

    const groupingMap = groupingMaps[config.name as keyof typeof groupingMaps];
    if (groupingMap) {
      numeratorGroupByValue = groupingMap[numeratorAggregator.groupBy!];
    } else {
      numeratorGroupByValue = (item as Record<string, any>)[numeratorAggregator.groupBy!];
    }

    this.populateNumeratorAggregatorMaps(
      numeratorAggregator.operation,
      numeratorValue,
      numeratorGroupByValue,
      numeratorValuesMap,
      numeratorCountMap
    );

    const denominatorValue =
      denominatorAggregator.field !== undefined
        ? (item as Record<string, any>)[denominatorAggregator.field]
        : undefined;
    let denominatorGroupByValue;

    if (groupingMap) {
      denominatorGroupByValue = groupingMap[denominatorAggregator.groupBy!];
    } else {
      denominatorGroupByValue = (item as Record<string, any>)[denominatorAggregator.groupBy!];
    }

    this.populateDenominatorAggregatorMaps(
      denominatorAggregator.operation,
      denominatorValue,
      denominatorGroupByValue,
      denominatorValuesMap,
      denominatorCountMap
    );

    if (aggregateFilter) {
      this.updateFilterMap(item, aggregateFilter, filterMap, config);
      this.populateOrganizationCountryMap(kpiChart.identifier, item, organizationCountryMap);
    }
  }

  private processBarChartValues(
    axisCalculationElements: AxisCalculationElement[] | undefined,
    config: KpiChartDataConfig,
    numeratorValuesMap: Map<string, number>,
    denominatorValuesMap: Map<string, number>,
    numeratorCountMap: Map<string, number>,
    denominatorCountMap: Map<string, number>,
    aggregatedValues: Record<string, AggregatedValue>,
    regularValueMap: Map<string, number>,
    regularCountMap: Map<string, number>,
    filterMap: Map<string, KpiChartAggregateFilter>,
    organizationCountryMap: Map<string, string>,
    kpiChart: KpiCategoricalChartData
  ) {
    // loop through each axis calculation element to process the aggregation
    axisCalculationElements?.forEach((axisCalculationElement) => {
      const numeratorAggregator = axisCalculationElement.numerator;
      const denominatorAggregator = axisCalculationElement.denominator;
      const axisAggregator = axisCalculationElement.axisAggregator;

      this.processKpiAggregation(
        config,
        numeratorAggregator,
        denominatorAggregator,
        axisAggregator,
        numeratorValuesMap,
        denominatorValuesMap,
        numeratorCountMap,
        denominatorCountMap,
        aggregatedValues,
        regularValueMap,
        regularCountMap,
        filterMap,
        organizationCountryMap,
        kpiChart.identifier
      );
    });
  }

  private processPieChartValues(
    valueCalculationElements: ValueCalculationElement[] | undefined,
    config: KpiChartDataConfig,
    numeratorValuesMap: Map<string, number>,
    denominatorValuesMap: Map<string, number>,
    numeratorCountMap: Map<string, number>,
    denominatorCountMap: Map<string, number>,
    aggregatedValues: Record<string, AggregatedValue>,
    regularValueMap: Map<string, number>,
    regularCountMap: Map<string, number>,
    filterMap: Map<string, KpiChartAggregateFilter>,
    organizationCountryMap: Map<string, string>,
    kpiChart: KpiCategoricalChartData
  ) {
    valueCalculationElements?.forEach((valueCalculationElement) => {
      const numeratorAggregator = valueCalculationElement.numerator;
      const denominatorAggregator = valueCalculationElement.denominator;
      const valueAggregator = valueCalculationElement.valueAggregator;

      this.processKpiAggregation(
        config,
        numeratorAggregator,
        denominatorAggregator,
        valueAggregator,
        numeratorValuesMap,
        denominatorValuesMap,
        numeratorCountMap,
        denominatorCountMap,
        aggregatedValues,
        regularValueMap,
        regularCountMap,
        filterMap,
        organizationCountryMap,
        kpiChart.identifier
      );
    });
  }

  private aggregatePieValues(
    item: KpiChartDataType,
    valueAggregator: KpiChartDataValueAggregator | undefined,
    distinctGroupedValueMap: Map<string, Set<number>>,
    config: KpiChartDataConfig,
    regularValueMap: Map<string, number>
  ) {
    if (
      valueAggregator &&
      valueAggregator.field !== undefined &&
      valueAggregator.groupBy !== undefined
    ) {
      const value = (item as Record<string, any>)[valueAggregator.field];
      let groupByValue;
      const groupingMaps = {
        pieDaysToCloseSiteFindingsByTimeRange: TIME_RANGE_MAP,
        pieDaysToPaySitesByTimeRange: TIME_RANGE_MAP,
        pieSubjectScreenFailRateByReason: KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP,
        pieDeviationRatePerSubjectByReason: KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP
      };

      const groupingMap = groupingMaps[config.name as keyof typeof groupingMaps];
      if (groupingMap) {
        groupByValue = groupingMap[valueAggregator.groupBy!];
      } else {
        groupByValue = (item as Record<string, any>)[valueAggregator.groupBy!];
      }

      if (value !== undefined && groupByValue !== undefined) {
        const operationType = valueAggregator.operation;
        if (operationType === 'count') {
          if (!distinctGroupedValueMap.has(groupByValue)) {
            distinctGroupedValueMap.set(groupByValue, new Set<number>());
          }
          distinctGroupedValueMap.get(groupByValue)?.add(value);
        } else if (operationType === 'sum') {
          const existingValue = regularValueMap.get(groupByValue) || 0;
          regularValueMap.set(groupByValue, existingValue + value);
        }
      }
    }
  }

  private evaluateDataFilterCondition(fieldValue: any, filter: KpiChartSrcDataFilter): boolean {
    switch (filter.condition) {
      case 'not_in':
        return filter.value instanceof Array && !filter.value.includes(fieldValue);
      case 'not_equal':
        return filter.value === 'None' ? fieldValue !== undefined : fieldValue !== filter.value;
      default:
        return false;
    }
  }

  // Populates a map of orgs and its country so they can be removed if country is filtered out
  private populateOrganizationCountryMap(
    identifier: string,
    item: KpiChartDataType,
    organizationCountryMap: Map<string, string>
  ) {
    if (identifier === 'barEnrolledByOrganization') {
      const orgGroupByValue = (item as Record<string, any>)['siteOrganization'];
      const countryValue = (item as Record<string, any>)['isoStdCountryName'];
      if (!organizationCountryMap.has(orgGroupByValue)) {
        organizationCountryMap.set(orgGroupByValue, countryValue);
      }
    }
  }

  private updateFilterMap(
    item: KpiChartDataType,
    filter: KpiChartDataValueFilter,
    filterMap: Map<string, KpiChartAggregateFilter>,
    config: KpiChartDataConfig
  ) {
    const filterValue = (item as Record<string, any>)[filter.field];
    let filterGroupByValue;
    // special case for charts where groupBy needs to be mapped to specific strings, eg. my_grouping -> 'Actual name of grouping'
    const groupingMaps = {
      barSubjectScreenFailRateByReason: KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP,
      pieSubjectScreenFailRateByReason: KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP,
      barEarlyTermByReason: KPI_EARLY_TERM_REASON_MAP,
      pieEarlyTermByReason: KPI_EARLY_TERM_REASON_MAP,
      barDeviationRatePerSubjectByReason: KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP,
      pieDeviationRatePerSubjectByReason: KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP,
      barDaysToCloseSiteFindingsByTimeRange: TIME_RANGE_MAP,
      pieDaysToCloseSiteFindingsByTimeRange: TIME_RANGE_MAP,
      barDaysToPaySitesByTimeRange: TIME_RANGE_MAP,
      pieDaysToPaySitesByTimeRange: TIME_RANGE_MAP,
      barEreqUsageForLabSamplesByRequestType: KPI_EREQ_USAGE_FOR_LAB_SAMPLES_BY_REQUEST_TYPE_MAP
    };

    const groupingMap = groupingMaps[config.name as keyof typeof groupingMaps];
    if (groupingMap) {
      filterGroupByValue = groupingMap[filter.groupBy!];
    } else {
      filterGroupByValue = (item as Record<string, any>)[filter.groupBy!];
    }

    if (filterValue !== undefined) {
      const filterEntry = filterMap.get(filterGroupByValue) || {
        total: 0,
        count: 0,
        field: filter.field,
        operation: filter.operation,
        condition: filter.condition,
        value: filter.value || 0,
        matchesCondition: false
      };
      filterEntry.total += filterValue;
      filterEntry.count += 1;
      filterMap.set(filterGroupByValue, filterEntry);
    }
  }

  private processKpiAggregation(
    config: KpiChartDataConfig,
    numeratorAggregator: KpiChartDataValueAggregator | undefined,
    denominatorAggregator: KpiChartDataValueAggregator | undefined,
    valueAggregator: KpiChartDataValueAggregator | undefined,
    numeratorValuesMap: Map<string, number>,
    denominatorValuesMap: Map<string, number>,
    numeratorCountMap: Map<string, number>,
    denominatorCountMap: Map<string, number>,
    values: Record<string, AggregatedValue>,
    regularValueMap: Map<string, number>,
    regularCountMap: Map<string, number>,
    filterMap: Map<string, KpiChartAggregateFilter>,
    organizationCountryMap: Map<string, string>,
    identifier: string
  ) {
    filterMap.forEach((entry) => {
      this.evaluateFilterCondition(entry);
    });
    // special case for numerator/denominator in xAxis
    if (
      (((config.type === 'bar' || config.type === 'pie') &&
        valueAggregator !== undefined &&
        valueAggregator.field !== undefined &&
        valueAggregator.field === this.KPI_NUMERATOR_DENOMINATOR_FIELD &&
        valueAggregator.operation === 'div') ||
        config.type === 'groupedBar') &&
      numeratorAggregator &&
      denominatorAggregator
    ) {
      // compute
      this.computeNumeratorDenominatorPercentValues(
        numeratorValuesMap,
        denominatorValuesMap,
        denominatorAggregator,
        numeratorAggregator,
        numeratorCountMap,
        denominatorCountMap,
        values,
        filterMap,
        organizationCountryMap,
        identifier,
        config.type === 'bar' || config.type === 'pie',
        valueAggregator?.type === 'percent'
      );
    } else {
      // normal cases where there is no numerator/denominator in xAxis or operation is not numerator/denominator
      this.processRegularValues(
        regularValueMap,
        numeratorAggregator,
        denominatorAggregator,
        valueAggregator,
        numeratorValuesMap,
        denominatorValuesMap,
        numeratorCountMap,
        denominatorCountMap,
        regularCountMap,
        values,
        filterMap,
        organizationCountryMap,
        identifier
      );
    }
  }

  private processSimplePieChartValues(
    distinctGroupedValueMap: Map<string, Set<number>>,
    config: KpiChartDataConfig,
    values: Record<string, AggregatedValue>
  ) {
    const valuesMapForPie = new Map<string, number>();

    distinctGroupedValueMap.forEach((setOfValues, category) => {
      valuesMapForPie.set(category, setOfValues.size);
    });
    if (config.isPercent) {
      const totalCountInData = Array.from(valuesMapForPie.values()).reduce(
        (sum, current) => sum + current,
        0
      );
      valuesMapForPie.forEach((count, category) => {
        const percentValue = (count / totalCountInData) * 100;
        values[category] = { value: Math.round(percentValue), numerator: 0, denominator: 0 };
      });
    }
  }

  private evaluateFilterCondition(entry: KpiChartAggregateFilter) {
    let meetsCondition = false;
    const calculatedValue = this.calculateFilterValue(entry.total, entry.count, entry.operation);
    if (calculatedValue !== undefined) {
      meetsCondition = this.evaluateCondition(calculatedValue, entry.value, entry.condition);
      entry.matchesCondition = meetsCondition;
    }
  }

  private evaluateCondition(
    calculatedValue: number,
    value: string | number,
    condition?: KpiChartCondition
  ): boolean {
    switch (condition) {
      case 'greater_than':
        return calculatedValue > Number(value);
      case 'not_equal':
        return value === 'None' ? calculatedValue !== undefined : calculatedValue !== Number(value);
      default:
        return false;
    }
  }

  private calculateFilterValue(total: number, count: number, operation?: KpiChartOperation) {
    let calculatedValue = 0;
    if (operation === 'avg') {
      calculatedValue = count > 0 ? total / count : 0;
    } else if (operation === 'sum') {
      calculatedValue = total;
    }
    return calculatedValue;
  }

  private processRegularValues(
    regularValueMap: Map<string, number>,
    numeratorAggregator: KpiChartDataValueAggregator | undefined,
    denominatorAggregator: KpiChartDataValueAggregator | undefined,
    axisAggregator: KpiChartDataValueAggregator | undefined,
    numeratorValuesMap: Map<string, number>,
    denominatorValuesMap: Map<string, number>,
    numeratorCountMap: Map<string, number>,
    denominatorCountMap: Map<string, number>,
    regularCountMap: Map<string, number>,
    values: Record<string, AggregatedValue>,
    filterMap: Map<string, KpiChartAggregateFilter>,
    organizationCountryMap: Map<string, string>,
    identifier: string
  ) {
    regularValueMap.forEach((value, category) => {
      // Check that the aggregator group by correspond with the category for bar otherwise skip to avoid unnecessary calculations
      const groupingMaps = {
        barSubjectScreenFailRateByReason: KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP,
        pieSubjectScreenFailRateByReason: KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP,
        barDeviationRatePerSubjectByReason: KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP,
        pieDeviationRatePerSubjectByReason: KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP,
        barDaysToCloseSiteFindingsByTimeRange: TIME_RANGE_MAP,
        pieDaysToCloseSiteFindingsByTimeRange: TIME_RANGE_MAP,
        barDaysToPaySitesByTimeRange: TIME_RANGE_MAP,
        pieDaysToPaySitesByTimeRange: TIME_RANGE_MAP,
        barEreqUsageForLabSamplesByRequestType: KPI_EREQ_USAGE_FOR_LAB_SAMPLES_BY_REQUEST_TYPE_MAP
      };

      const groupingMap = groupingMaps[identifier as keyof typeof groupingMaps];
      if (groupingMap) {
        const groupByString =
          this.KPI_CHARTS_WITH_GROUPED_VALUES_AND_NO_NUMERATOR_DENOMINATOR.includes(identifier)
            ? groupingMap[axisAggregator!.groupBy!]
            : groupingMap[numeratorAggregator!.groupBy!];
        if (category !== groupByString) {
          return;
        }
      }
      const skip = this.shouldSkipCategory(identifier, category, filterMap, organizationCountryMap);
      if (skip) {
        return;
      }
      let finalValue = 0;
      let finalNumValue = 0;
      let finalDenomValue = 0;
      // calculate both numerator and denominator aggregated values
      if (numeratorAggregator && denominatorAggregator) {
        const numValue = numeratorValuesMap.get(category) || 0;
        const denomValue = denominatorValuesMap.get(category) || 0;

        finalNumValue = this.calculateNumeratorValue(
          numeratorAggregator,
          finalNumValue,
          numValue,
          numeratorCountMap,
          category
        );

        finalDenomValue = this.calculateDenominatorValue(
          denominatorAggregator,
          finalDenomValue,
          denomValue,
          denominatorCountMap,
          category
        );
      }

      if (axisAggregator !== undefined && axisAggregator.field !== undefined) {
        // if value calculation does not require numerator/denominator aggregates
        finalValue = this.calculateFinalValue(
          finalValue,
          value,
          regularCountMap,
          category,
          axisAggregator
        );
      }

      values[category] = {
        value: finalValue,
        numerator: finalNumValue,
        denominator: finalDenomValue
      };
    });
  }

  private shouldSkipCategory(
    identifier: string,
    category: string,
    filterMap: Map<string, KpiChartAggregateFilter>,
    organizationCountryMap: Map<string, string>
  ) {
    let skip = false;
    if (filterMap.size === 0) {
      return skip;
    } else if (identifier === 'barEnrolledByOrganization') {
      const country = organizationCountryMap.get(category);
      if (country && filterMap.size > 0 && filterMap.get(country)?.matchesCondition === false) {
        skip = true;
      }
    } else if (filterMap.size > 0 && filterMap.get(category)?.matchesCondition === false) {
      skip = true;
    }
    return skip;
  }

  private calculateFinalValue(
    finalValue: number,
    value: number,
    regularCountMap: Map<string, number>,
    category: string,
    axisAggregator: KpiChartDataValueAggregator | undefined
  ) {
    if (axisAggregator !== undefined && axisAggregator.operation === 'avg') {
      finalValue = parseFloat((value / regularCountMap.get(category)!).toFixed(2));
    } else if (axisAggregator !== undefined && axisAggregator.operation === 'count') {
      finalValue = regularCountMap.get(category)!;
    } else {
      finalValue = value;
    }
    return finalValue;
  }

  private calculateDenominatorValue(
    denominatorAggregator: KpiChartDataValueAggregator,
    finalDenomValue: number,
    denomValue: number,
    denominatorCountMap: Map<string, number>,
    category: string
  ) {
    switch (denominatorAggregator.operation) {
      case 'sum':
        finalDenomValue = denomValue;
        break;
      case 'avg':
        finalDenomValue = parseFloat((denomValue / denominatorCountMap.get(category)!).toFixed(2));
        break;
      case 'count':
        finalDenomValue = denominatorCountMap.get(category)!;
        break;
    }
    return finalDenomValue;
  }

  private calculateNumeratorValue(
    numeratorAggregator: KpiChartDataValueAggregator,
    finalNumValue: number,
    numValue: number,
    numeratorCountMap: Map<string, number>,
    category: string
  ) {
    switch (numeratorAggregator.operation) {
      case 'sum':
        finalNumValue = numValue;
        break;
      case 'avg':
        finalNumValue = parseFloat((numValue / numeratorCountMap.get(category)!).toFixed(2));
        break;
      case 'count':
        finalNumValue = numeratorCountMap.get(category)!;
        break;
    }
    return finalNumValue;
  }

  private computeNumeratorDenominatorPercentValues(
    numeratorValuesMap: Map<string, number>,
    denominatorValuesMap: Map<string, number>,
    denominatorAggregator: KpiChartDataValueAggregator,
    numeratorAggregator: KpiChartDataValueAggregator,
    numeratorCountMap: Map<string, number>,
    denominatorCountMap: Map<string, number>,
    values: Record<string, AggregatedValue>,
    filterMap: Map<string, KpiChartAggregateFilter>,
    organizationCountryMap: Map<string, string>,
    identifier: string,
    calculateFinalValue: boolean,
    operationIsPercent: boolean
  ) {
    numeratorValuesMap.forEach((numValue, category) => {
      // Check that the numerator aggregator group by corresponds with the category for pie otherwise skip to avoid unnecessary calculations
      const groupingMaps = {
        pieSubjectScreenFailRateByReason: KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP,
        barSubjectScreenFailRateByReason: KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP,
        pieEarlyTermByReason: KPI_EARLY_TERM_REASON_MAP,
        barEarlyTermByReason: KPI_EARLY_TERM_REASON_MAP,
        barDeviationRatePerSubjectByReason: KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP,
        pieDeviationRatePerSubjectByReason: KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP
      };

      const groupingMap = groupingMaps[identifier as keyof typeof groupingMaps];
      if (groupingMap) {
        const categoryString = groupingMap[numeratorAggregator!.groupBy!];
        if (category !== categoryString) {
          return;
        }
      }
      const skip = this.shouldSkipCategory(identifier, category, filterMap, organizationCountryMap);
      if (skip) {
        return;
      }
      const denomValue =
        denominatorValuesMap.get(denominatorAggregator.groupBy! ? category : 'default') ||
        undefined;
      let finalNumValue = 0;
      let finalDenomValue = undefined;
      let finalValue = 0;

      //calculate numerator aggregated value
      finalNumValue = this.aggregateNumeratorValue(
        numeratorAggregator,
        finalNumValue,
        numValue,
        numeratorCountMap,
        category
      );

      //calculate denominator aggregated value
      finalDenomValue = this.aggregateDenominatorValue(
        denominatorAggregator,
        finalDenomValue,
        denomValue,
        denominatorCountMap,
        category
      );

      if (calculateFinalValue && finalDenomValue !== undefined && finalDenomValue !== 0) {
        // check if operation is percent or number
        if (operationIsPercent) {
          finalValue = parseFloat(
            (parseFloat((finalNumValue / finalDenomValue).toFixed(4)) * 100).toFixed(4)
          );
        } else {
          finalValue = parseFloat((finalNumValue / finalDenomValue).toFixed(2));
        }
      }

      if (finalDenomValue !== undefined) {
        values[category] = {
          value: finalValue,
          numerator: finalNumValue,
          denominator: finalDenomValue
        };
      }
    });
  }

  private aggregateDenominatorValue(
    denominatorAggregator: KpiChartDataValueAggregator,
    finalDenomValue: number | undefined,
    denomValue: number | undefined,
    denominatorCountMap: Map<string, number>,
    category: string
  ) {
    switch (denominatorAggregator.operation) {
      case 'sum':
        finalDenomValue = denomValue;
        break;
      case 'avg':
        finalDenomValue =
          denomValue !== undefined
            ? parseFloat((denomValue / denominatorCountMap.get(category)!).toFixed(2))
            : undefined;
        break;
      case 'count':
        finalDenomValue = denominatorCountMap.get(category)!;
        break;
    }
    return finalDenomValue;
  }

  private aggregateNumeratorValue(
    numeratorAggregator: KpiChartDataValueAggregator,
    finalNumValue: number,
    numValue: number,
    numeratorCountMap: Map<string, number>,
    category: string
  ) {
    switch (numeratorAggregator.operation) {
      case 'sum':
        finalNumValue = numValue;
        break;
      case 'avg':
        finalNumValue = parseFloat((numValue / numeratorCountMap.get(category)!).toFixed(2));
        break;
      case 'count':
        finalNumValue = numeratorCountMap.get(category)!;
        break;
    }
    return finalNumValue;
  }

  private setTargetValues(config: KpiChartDataConfig, kpiChart: KpiCategoricalChartData) {
    if (['bar', 'groupedBar'].includes(config.type)) {
      const onTrack = kpiChart.config?.onTrackValue;
      const onTarget = kpiChart.config?.targetValue;
      const isPercent = kpiChart.config?.isPercent;

      if (onTrack != undefined) {
        kpiChart.onTrack = isPercent ? onTrack * 100 : onTrack;
      }

      if (onTarget != undefined) {
        kpiChart.onTarget = isPercent ? onTarget * 100 : onTarget;
      }
    }
  }

  private sortValues(
    kpiChart: KpiCategoricalChartData,
    values: Record<string, AggregatedValue>,
    type: string
  ) {
    const sortedValues = { ...values };
    const sortedEntries = Object.entries(sortedValues);
    const sortByField = type === 'groupedBar' ? 'numerator' : 'value';
    switch (kpiChart.sort) {
      case 'asc':
        sortedEntries.sort((a, b) => a[1][sortByField] - b[1][sortByField]);
        break;
      case 'desc':
        sortedEntries.sort((a, b) => b[1][sortByField] - a[1][sortByField]);
        break;
      case 'alphabetical':
        sortedEntries.sort((a, b) => a[0].localeCompare(b[0]));
        break;
      default:
        break;
    }

    // need them in reverse for echarts to display in correct order
    sortedEntries.reverse();
    return Object.fromEntries(sortedEntries);
  }

  private populateNumeratorAggregatorMaps(
    operation: KpiChartOperation | undefined,
    numeratorValue: any,
    numeratorGroupByValue: any,
    valueMap: Map<string, number>,
    countMap: Map<string, number>,
    countArray: any[] = []
  ) {
    if (numeratorValue !== undefined && numeratorGroupByValue !== undefined) {
      if (operation === 'count') {
        // to handle unique count, we need to check if the value is already in the array
        if (!countArray.includes(numeratorValue)) {
          countArray.push(numeratorValue);
          countMap.set(numeratorGroupByValue, (countMap.get(numeratorGroupByValue) || 0) + 1);
          // for count, the value is the same as the count, just add it to map since we loop through all items later
          valueMap.set(numeratorGroupByValue, (valueMap.get(numeratorGroupByValue) || 0) + 1);
        }
      } else {
        valueMap.set(
          numeratorGroupByValue,
          (valueMap.get(numeratorGroupByValue) || 0) + numeratorValue
        );

        if (operation === 'avg') {
          // for avg, we need to keep track of the count
          countMap.set(numeratorGroupByValue, (countMap.get(numeratorGroupByValue) || 0) + 1);
        }
      }
    }
  }

  private populateDenominatorAggregatorMaps(
    operation: string | undefined,
    denominatorValue: any,
    denominatorGroupByValue: any,
    valuesMap: Map<string, number>,
    countMap: Map<string, number>
  ) {
    if (denominatorValue !== undefined && denominatorGroupByValue !== undefined) {
      valuesMap.set(
        denominatorGroupByValue,
        (valuesMap.get(denominatorGroupByValue) || 0) + denominatorValue
      );
      if (operation === 'avg') {
        // for avg, we need to keep track of the count
        countMap.set(denominatorGroupByValue, (countMap.get(denominatorGroupByValue) || 0) + 1);
      }
      return;
    } else if (denominatorValue !== undefined) {
      // if no groupBy is defined, we aggregate all values under 'default' key
      valuesMap.set('default', (valuesMap.get('default') || 0) + denominatorValue);
      return;
    }
    return;
  }

  private extractConfig(config: KpiChartDataConfig) {
    const allowedTypes = ['bar', 'pie', 'groupedBar'];
    if (allowedTypes.includes(config.type)) {
      const axisCalculationElements: AxisCalculationElement[] = [];
      const hasValue = Array.isArray(config.value) && config.value.length > 0;
      // loop xAxis and extract numerator and denominator into an array of objects of numerator and denominator
      if (config.xAxis) {
        config.xAxis.forEach((xAxis) => {
          const axisCalculationElement = {} as AxisCalculationElement;
          if (xAxis.numerator) {
            axisCalculationElement.numerator = {
              field: xAxis.numerator.field,
              operation: xAxis.numerator.operation,
              groupBy: xAxis.numerator.groupBy
            };
          }
          if (xAxis.denominator) {
            axisCalculationElement.denominator = {
              field: xAxis.denominator.field,
              operation: xAxis.denominator.operation,
              groupBy: xAxis.denominator.groupBy
            };
          }

          if (xAxis.filter) {
            axisCalculationElement.filter = {
              field: xAxis.filter.field,
              groupBy: xAxis.filter.groupBy,
              operation: xAxis.filter.operation,
              condition: xAxis.filter.condition,
              value: xAxis.filter.value
            };
          }
          axisCalculationElement.hasAggLabel = xAxis.hasAggLabel;

          axisCalculationElement.axisAggregator = {
            field: xAxis.field,
            operation: xAxis.operation,
            type: xAxis.type,
            groupBy: xAxis.groupBy
          };

          axisCalculationElements.push(axisCalculationElement);
        });
      }

      const valueCalculationElements: ValueCalculationElement[] = [];
      if (hasValue && config.value) {
        config.value.forEach((value) => {
          const valueCalculationElement = {} as ValueCalculationElement;
          if (value.numerator) {
            valueCalculationElement.numerator = {
              field: value.numerator.field,
              operation: value.numerator.operation,
              groupBy: value.numerator.groupBy
            };
          } else {
            valueCalculationElement.numerator = undefined;
          }
          if (value.denominator) {
            valueCalculationElement.denominator = {
              field: value.denominator.field,
              operation: value.denominator.operation,
              groupBy: value.denominator.groupBy
            };
          } else {
            valueCalculationElement.denominator = undefined;
          }

          if (value.filter) {
            valueCalculationElement.filter = {
              field: value.filter.field,
              groupBy: value.filter.groupBy,
              operation: value.filter.operation,
              condition: value.filter.condition,
              value: value.filter.value
            };
          } else {
            valueCalculationElement.filter = undefined;
          }

          valueCalculationElement.valueAggregator = {
            field: value.field,
            operation: value.operation,
            type: value.type,
            groupBy: value.groupBy
          };

          valueCalculationElements.push(valueCalculationElement);
        });
      }

      const srcDataFilters = config.dataFilters ? config.dataFilters : undefined;
      return {
        axisCalculationElements: axisCalculationElements,
        valueCalculationElements: valueCalculationElements,
        dataFilters: srcDataFilters
      };
    }
    return undefined;
  }
}
